import questionIcon from '@/public/images/icons/question-mark.svg';
import { useLocalization } from '@/services/localizationService';
import { StickyTooltip } from '@/ui/stickyTooltip';
import { useCallback, useState } from 'react';
import editCircle from '../../../../public/images/icons/edit-circle.svg';
import arrowLeft from '../../../../public/images/icons/right-arrow-white.svg';
import { badgeTypes } from '../../../../ui/badge';
import { DropdownProps } from './dropdownTypes';

export default function Dropdown({
  id,
  title,
  icon,
  iconPath,
  badge,
  tooltip,
  isOpen = false,
  children,
  hasChangedDefaultChoice,
  shouldHighlight,
  dropdownButtonDivId,
  isTutorialActive,
}: DropdownProps) {
  const { t } = useLocalization();
  const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);

  const onDropdownClick = useCallback(() => {
    if (isTutorialActive) {
      return;
    }

    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen, isTutorialActive]);

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      className={`w-[100%] flex flex-col cursor-pointer overflow-hidden transition-all ${
        isDropdownOpen ? 'h-[auto]' : 'h-[44px]'
      }`}
    >
      <div
        onClick={onDropdownClick}
        id={dropdownButtonDivId}
        className={`w-[100%] flex justify-between items-center rounded-lg py-1 hover:bg-secondary-gray ${
          shouldHighlight ? 'bg-secondary-gray z-50' : ''
        }`}
      >
        <div className="flex items-center">
          {icon && (
            <img {...icon} className="w-9 h-9" alt={`${id} dropdown icon`} />
          )}
          {iconPath && (
            <img
              src={iconPath}
              className="w-9 h-9"
              alt={`${id} dropdown icon`}
            />
          )}
          <div className="flex flex-col justify-between ms-2.5">
            {badge && (
              <p
                className={`uppercase text-[9px] font-bold text-left ${
                  badge === badgeTypes.BadgeVariants.New
                    ? 'text-illustrationsOnly-salmon'
                    : badge === badgeTypes.BadgeVariants.Business
                    ? 'text-secondary-purple'
                    : badge === badgeTypes.BadgeVariants.Beta
                    ? 'text-illustrationsOnly-lightYellow'
                    : 'text-white'
                }`}
              >
                {badge}
              </p>
            )}
            <div className="flex items-center">
              <p className="SB12">{t(title, {}, 'capitalize')}</p>
              {tooltip && (
                <StickyTooltip
                  placement="right-center"
                  tooltipContent={
                    <div className="translate-y-[-30%] translate-x-[-5%] bg-white text-black fixed w-[200px] z-[55] rounded-xl p-1.5">
                      <div className="w-[100%] h-[124px] rounded-[8px] mb-2 overflow-hidden flex justify-center items-center">
                        <video
                          height="100%"
                          width="auto"
                          autoPlay
                          muted
                          loop
                          className="max-w-none w-auto h-[100%]"
                          src={tooltip.mediaUrl}
                        />
                      </div>
                      <div className="p-1.5 mb-1">
                        <p className="SB15 text-gray-450 mb-1">
                          {t(tooltip.title, {}, 'capitalize')}
                        </p>
                        <p className="text-gray-400 M12">{tooltip.body}</p>
                      </div>
                    </div>
                  }
                >
                  <img
                    {...questionIcon}
                    alt="help icon"
                    className="mt-0.5 ms-1.5"
                  />
                </StickyTooltip>
              )}
            </div>
          </div>
        </div>
        <div className="flex">
          {hasChangedDefaultChoice && (
            <img {...editCircle} className="me-2" alt="choice changed icon" />
          )}
          <img
            {...arrowLeft}
            className={`transition-all ${isDropdownOpen && 'rotate-90'}`}
            alt="arrow icon"
          />
        </div>
      </div>
      {children}
    </div>
  );
}
